import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { StatCard } from '../components/StatCard';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { IcReceipt, IcDollar, IcFile } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getPayablesSummary, getPayables, runPayables, payablePdfUrl, ApiError, type PayableRow } from '../lib/api';
import { PAYABLE_STATUS } from '../lib/types';
import { fmtCents, fmtCents0, fmtDate } from '../lib/format';

const shortId = (id: string) => `#${id.slice(-6)}`;

const DAY = 86_400_000;

/** Start (UTC midnight) of the Friday-aligned week containing `d` — matches the
 *  app's Friday-aligned week convention (Pharmacy Volume, metrics). */
function fridayWeekStart(d: Date): Date {
  const s = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  s.setUTCDate(s.getUTCDate() - ((s.getUTCDay() - 5 + 7) % 7));
  return s;
}
/** The most recent `n` COMPLETED Friday-week starts, newest first. The current,
 *  still-in-progress week is excluded on purpose: its window would end next
 *  Friday, and the run endpoint rejects a future-dated `to`. */
function recentCompletedWeeks(n: number): Date[] {
  const cur = fridayWeekStart(new Date());
  return Array.from({ length: n }, (_, i) => new Date(cur.getTime() - (i + 1) * 7 * DAY));
}
const weekIso = (d: Date) => d.toISOString();
const weekLabel = (iso: string) =>
  `Wk of ${new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
const dayLabel = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

type RangeMode = 'weeks' | 'catchup';

export const PharmacyPayablesPage = () => {
  const { can } = useAuth();
  const summary = useFetch(getPayablesSummary);
  const payables = useFetch(() => getPayables());
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  const weeks = recentCompletedWeeks(16);
  const [mode, setMode] = useState<RangeMode>('weeks');
  // One week is the default unit of work. `toWeek` only participates once the
  // user opts into a span — otherwise picking a week silently widened the run
  // to [that week, default week], which billed months of fills at once.
  const [span, setSpan] = useState(false);
  const [fromWeek, setFromWeek] = useState(weekIso(weeks[0]));
  const [toWeek, setToWeek] = useState(weekIso(weeks[0]));

  /** Resolved window [start, end) — order-independent across the two picks. */
  function coverage() {
    const a = new Date(fromWeek).getTime();
    const b = span ? new Date(toWeek).getTime() : a;
    return { start: new Date(Math.min(a, b)), end: new Date(Math.max(a, b) + 7 * DAY) };
  }

  function coverageLabel() {
    const { start, end } = coverage();
    const weekCount = Math.round((end.getTime() - start.getTime()) / (7 * DAY));
    return `Covers ${dayLabel(start)} – ${dayLabel(new Date(end.getTime() - DAY))} (${weekCount} week${weekCount === 1 ? '' : 's'})`;
  }

  function pickFromWeek(iso: string) {
    setFromWeek(iso);
    // Keep the span collapsed to a single week until it's deliberately widened.
    if (!span) setToWeek(iso);
  }

  function windowBody() {
    if (mode === 'catchup') return {};
    const { start, end } = coverage();
    return { from: start.toISOString(), to: end.toISOString() };
  }

  async function run(draft = false) {
    setRunning(true);
    setRunMsg(null);
    try {
      const r = await runPayables({ draft, ...windowBody() });
      const verb = draft ? 'Drafted' : 'Issued';
      const tail = draft ? ' — review, then finalize to approve for payment.' : '.';
      const deferred = r.deferred > 0 ? ` · ${r.deferred} fills unpriced (add costs)` : '';
      setRunMsg(
        `${verb} ${r.invoiceCount} payable${r.invoiceCount === 1 ? '' : 's'} across ${r.payeeCount} pharmacies — ${r.fillsPaid} fills (${fmtCents0(r.grossCents)})${tail}${deferred}`,
      );
      payables.reload();
      summary.reload();
    } catch (err) {
      setRunMsg(err instanceof ApiError ? err.message : 'Run failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Page>
      <PageHeader
        title="Pharmacy Payables"
        sub="Fills shipped by 3rd-party pharmacies × our cost table · one aggregate invoice per pharmacy · we pay by ACH/wire"
        actions={
          can('billing:run') ? (
            <>
              <button className="btn btn--ghost" onClick={() => run(true)} disabled={running}>
                {running ? 'Running…' : 'Generate drafts'}
              </button>
              <button className="btn btn--primary" onClick={() => run(false)} disabled={running}>
                {running ? 'Running…' : 'Run payables'}
              </button>
            </>
          ) : undefined
        }
      />

      {runMsg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(59,130,246,.08)', color: 'var(--status-in-progress-ink,#1d4ed8)', fontSize: 13 }}>
          {runMsg}
        </div>
      )}

      <PCard style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-muted-fg)' }}>COVERAGE</span>
        <select className="select" value={mode} onChange={(e) => setMode(e.target.value as RangeMode)}>
          <option value="weeks">Specific weeks</option>
          <option value="catchup">All uninvoiced (last 90 days)</option>
        </select>
        {mode === 'weeks' && (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>Week</span>
            <select className="select" value={fromWeek} onChange={(e) => pickFromWeek(e.target.value)}>
              {weeks.map((w) => <option key={weekIso(w)} value={weekIso(w)}>{weekLabel(weekIso(w))}</option>)}
            </select>
            {span ? (
              <>
                <span style={{ color: 'var(--p-muted-fg)' }}>–</span>
                <select className="select" value={toWeek} onChange={(e) => setToWeek(e.target.value)}>
                  {weeks.map((w) => <option key={weekIso(w)} value={weekIso(w)}>{weekLabel(weekIso(w))}</option>)}
                </select>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setSpan(!span);
                if (span) setToWeek(fromWeek);
              }}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--p-muted-fg)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {span ? 'single week' : '+ span multiple weeks'}
            </button>
          </span>
        )}
        <span style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>
          {mode === 'weeks'
            ? `${coverageLabel()} · already-paid fills are skipped.`
            : 'Every shipped fill not yet on a payable, from the last 90 days.'}
        </span>
      </PCard>

      <div className="grid grid-3">
        <StatCard tone="in-progress" icon={IcDollar} label="Owed to pharmacies" value={fmtCents0(summary.data?.owedCents ?? 0)} sub={`${summary.data?.openCount ?? 0} open`} />
        <StatCard tone="neutral" icon={IcFile} label="In draft" value={fmtCents0(summary.data?.draftCents ?? 0)} sub={`${summary.data?.draftCount ?? 0} drafts`} />
        <StatCard tone="success" icon={IcReceipt} label="Paid to date" value={fmtCents0(summary.data?.paidCents ?? 0)} />
      </div>

      <PCard style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div className="p-stat__icon p-stat__icon--in-progress">
          <IcReceipt size={18} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>
          Payables aggregate every fill on an order the pharmacy completed in the window, priced against the
          pharmacy cost table — maintain rates under{' '}
          <strong style={{ color: 'var(--p-foreground)' }}>Pharmacy Costs</strong>. Shipping isn't included (we pay
          FedEx directly). Drafts claim their fills so a fill is never paid twice; finalize to approve, then mark paid
          when remitted.
        </div>
      </PCard>

      <div>
        <div className="section-title">Payable invoices</div>
        <Async state={payables} isEmpty={(d) => d.payables.length === 0} emptyLabel="No payables yet — run to generate.">
          {({ payables: rows }) => <PayableTable payables={rows} />}
        </Async>
      </div>
    </Page>
  );
};

const PayableTable = ({ payables }: { payables: PayableRow[] }) => (
  <PCard>
    <table className="p-table">
      <thead>
        <tr>
          <th>Payable</th>
          <th>Pharmacy</th>
          <th>Period</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Due</th>
          <th style={{ textAlign: 'right' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {payables.map((p) => {
          const s = PAYABLE_STATUS[p.status];
          return (
            <tr key={p.id}>
              <td className="mono"><Link to={`/payables/${p.id}`}>{p.invoiceNumber ?? shortId(p.id)}</Link></td>
              <td>{p.payeeName}</td>
              <td className="muted">{p.period.label}</td>
              <td className="tabular">{fmtCents(p.totalCents)}</td>
              <td><StatusPill tone={s.tone}>{s.label}</StatusPill></td>
              <td className="muted">{p.dueAt ? fmtDate(p.dueAt) : '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <a className="btn" href={payablePdfUrl(p.id)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                  PDF
                </a>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </PCard>
);
